import { useEffect, useRef } from 'react';
import { IDX_BROKER_SUBDOMAIN } from '../constants/idx';

/**
 * Injects an IDX Broker widget's <script> into a container div.
 *
 * IDX Broker widgets follow this shape:
 *   <div id="idxwidgetsrc{UID}"></div>
 *   <script src="//{subdomain}/idx/widgets/{UID}"></script>
 *
 * The script writes into the div with the matching id on load. We build it
 * dynamically instead of pasting raw HTML so React state stays clean.
 *
 * Pass an empty `widgetId` to no-op (useful before the client provides codes).
 *
 * @param {string} widgetId  IDX Broker widget UID
 * @returns {React.RefObject<HTMLDivElement>} attach to the container div
 */
export function useIdxWidget(widgetId) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!widgetId || !containerRef.current) return undefined;
    const scriptId = `idxwidgetsrc-${widgetId}`;
    if (document.getElementById(scriptId)) return undefined;

    // IDX Broker requires the target div to exist with a specific id BEFORE
    // the script loads.
    const targetDivId = `idxwidgetsrc${widgetId}`;
    let targetDiv = containerRef.current.querySelector(`#${targetDivId}`);
    if (!targetDiv) {
      targetDiv = document.createElement('div');
      targetDiv.id = targetDivId;
      containerRef.current.appendChild(targetDiv);
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.src = `//${IDX_BROKER_SUBDOMAIN}/idx/widgets/${widgetId}`;
    document.body.appendChild(script);

    return () => {
      // Leave the div in place (IDX may hydrate it async); clean up only
      // the script so re-mounts inject fresh.
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [widgetId]);

  return containerRef;
}
